<template>
    <b-modal
        v-model="modal"
        title="Add Balance Record"
        hide-footer
        centered
        @hidden="resetData"
    >
        <b-form @submit.prevent="onSubmit">
            <b-form-group label="Date">
                <b-form-datepicker
                    v-model="form.date"
                    today-button
                    reset-button
                    close-button
                    :date-format-options="{
                        year: 'numeric',
                        month: 'numeric',
                        day: 'numeric',
                    }"
                    locale="zh-TW"
                ></b-form-datepicker>
            </b-form-group>
            <b-form-group label="Record Type">
                <b-form-select
                    v-model="form.recordType"
                    :options="recordTypeOptions"
                    required
                ></b-form-select>
            </b-form-group>
            <b-form-group label="Payment Type">
                <b-form-select
                    v-model="form.paymentType"
                    :options="paymentTypeOptions"
                    required
                ></b-form-select>
            </b-form-group>
            <b-form-group label="Amount">
                <b-form-input v-model="form.amount" required></b-form-input>
            </b-form-group>
            <b-form-group label="Discount">
                <b-form-input
                    v-model="form.discount"
                    placeholder="e.g. 0.1"
                ></b-form-input>
            </b-form-group>
            <b-form-group label="Remark">
                <b-form-textarea
                    v-model="form.remark"
                    placeholder="Remark"
                ></b-form-textarea>
            </b-form-group>
            <fieldset>
                <legend
                    tabindex="-1"
                    class="bv-no-focus-ring col-form-label pt-0"
                >
                    Balance
                </legend>
                <legend style="text-align: end">{{ balance }}</legend>
                <!-- <label></label> -->
            </fieldset>
            <b-button type="submit" block variant="primary">Save</b-button>
        </b-form>
    </b-modal>
</template>

<script>
import { addBalanceRecord } from '@/services/aws.service';

const INIT_FORM_DATA = {
    date: null,
    recordType: null,
    paymentType: null,
    amount: null,
    discount: null,
    remark: null,
};
export default {
    name: 'AddBalanceRecordModal',
    props: {
        value: {
            type: Boolean,
            required: true,
            default: false,
        },
        id: {
            type: String,
            required: true,
        },
        firstRecord: {
            type: [Object, null],
            required: false,
            default() {
                return null;
            },
        },
    },
    computed: {
        modal: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            },
        },
        balance: {
            get() {
                if (
                    !this.form.recordType ||
                    !this.form.paymentType ||
                    !this.form.amount
                ) {
                    return this.firstRecord?.balance || 0;
                }

                let fee = 0.0;
                let amount = parseFloat(this.form.amount);
                let discountAmount = 0.0;
                let paymentAmount = 0.0;

                if (
                    this.form.recordType === 'RECEIVABLE' ||
                    this.form.recordType === 'COLLECT'
                ) {
                    // 負項
                    amount = -amount;

                    // 計算折扣金額
                    discountAmount = -this.round(
                        amount * (this.form.discount || 0),
                        2
                    );

                    // 計算手續費
                    fee = 0;
                } else {
                    // 計算手續費
                    if (this.form.paymentType === 'REMITTANCE') {
                        fee = 0;
                    } else if (
                        this.form.recordType === 'PREPAY' &&
                        this.form.paymentType === 'VIRTUAL_CURRENCY'
                    ) {
                        fee = -this.round(amount / 0.97 - amount, 2);
                    } else if (
                        this.form.recordType === 'PREPAY' &&
                        this.form.paymentType === 'HUIONE'
                    ) {
                        fee = -this.round(amount / 0.985 - amount, 2);
                    } else if (
                        this.form.recordType === 'MONTHLY' &&
                        this.form.paymentType === 'VIRTUAL_CURRENCY'
                    ) {
                        fee = -this.round(amount - amount * 0.97, 2);
                    } else if (
                        this.form.recordType === 'MONTHLY' &&
                        this.form.paymentType === 'HUIONE'
                    ) {
                        fee = -this.round(amount - amount * 0.985, 2);
                    }
                }

                // 計算收款金額
                paymentAmount = this.round(amount + discountAmount + fee, 2);

                // 計算餘額
                if (this.firstRecord) {
                    return `${this.firstRecord.balance}${
                        amount == 0
                            ? ''
                            : ` ${amount > 0 ? ' + ' : ' - '} ${this.round(
                                  Math.abs(amount),
                                  2
                              )}`
                    }${
                        discountAmount == 0
                            ? ''
                            : ` + ${this.round(discountAmount, 2)}(discount)`
                    }${
                        fee == 0 ? '' : ` - ${-this.round(fee, 2)}(fee)`
                    } => ${this.round(
                        this.firstRecord.balance + paymentAmount,
                        2
                    )}`;
                } else {
                    return `0${
                        amount == 0
                            ? ''
                            : `${amount > 0 ? ' + ' : ' - '} ${this.round(
                                  Math.abs(amount),
                                  2
                              )}`
                    }${
                        discountAmount == 0
                            ? ''
                            : ` + ${this.round(discountAmount, 2)}(discount)`
                    }${
                        fee == 0 ? '' : ` - ${-this.round(fee, 2)}(fee)`
                    } => ${this.round(paymentAmount, 2)}`;
                }
            },
        },
    },
    data() {
        return {
            form: {
                ...INIT_FORM_DATA,
            },
            recordTypeOptions: [
                { value: 'RECEIVABLE', text: 'RECEIVABLE' },
                { value: 'COLLECT', text: 'COLLECT' },
                { value: 'PREPAY', text: 'PREPAY' },
                { value: 'MONTHLY', text: 'MONTHLY' },
                { value: 'CREDIT_MIGRATION', text: 'CREDIT_MIGRATION' },
                { value: 'CREDIT_POC', text: 'CREDIT_POC' },
            ],
            paymentTypeOptions: [
                { value: 'REMITTANCE', text: 'REMITTANCE' },
                { value: 'HUIONE', text: 'HUIONE' },
                { value: 'VIRTUAL_CURRENCY', text: 'VIRTUAL_CURRENCY' },
                { value: 'NA', text: 'NA' },
            ],
        };
    },
    methods: {
        resetData() {
            this.form = { ...INIT_FORM_DATA };
        },
        onSubmit() {
            this.addData()
                .then(() => {
                    this.$bvToast.toast(`Save success!`, {
                        title: 'Success',
                        autoHideDelay: 5000,
                        appendToast: true,
                        variant: 'success',
                        solid: false,
                        toaster: 'b-toaster-top-center',
                    });
                    this.$emit('onDone');
                    this.modal = false;
                })
                .catch((e) => {
                    const data = e.response?.data || {};

                    this.form.isCreateNewUser = !data.isCreatedUser;
                    const message = data.message || 'An error occurred!';
                    this.$bvToast.toast(message, {
                        title: 'Error',
                        autoHideDelay: 5000,
                        appendToast: true,
                        variant: 'danger',
                        solid: false,
                        toaster: 'b-toaster-top-center',
                    });
                });
        },
        async addData() {
            const form = { ...this.form };
            form.amount = parseFloat(form.amount) || 0;
            form.discount = parseFloat(form.discount) || 0;
            return addBalanceRecord(this.id, form);
        },
        round(value, decimals) {
            return (
                Math.round(value * Math.pow(10, decimals)) /
                Math.pow(10, decimals)
            );
        },
    },
};
</script>

<style scoped></style>
